import React from 'react';

import { DefaultContext } from '@apollo/client';

import { Nullable } from '@clearverify/types';

import {
  EmployerStaffDocument,
  EmployerStaffsListDocument,
  EmployerStaffUpdate,
  InvitedEmployerStaff,
  useEmployerStaffBillingInfoQuery,
  useEmployerStaffInviteByEmployerAdminMutation,
  useEmployerStaffInviteMutation,
  useEmployerStaffQuery,
  useEmployerStaffUpdateMutation,
} from 'src/api';

export const useEmployerStaff = (employerStaffId?: Nullable<string>) => {
  const { data: employerStaffQueryData, loading } = useEmployerStaffQuery({
    variables: {
      id: employerStaffId || '',
    },
    skip: !employerStaffId,
  });

  const employerId = employerStaffQueryData?.employerStaff?.employer.id;

  const { data: employerStaffBillingQueryData, loading: employerStaffBillingLoading } =
    useEmployerStaffBillingInfoQuery({
      variables: {
        id: employerStaffId || '',
      },
      skip: !employerStaffId,
    });

  const [employerStaffUpdateMutation, { loading: updating }] = useEmployerStaffUpdateMutation({
    refetchQueries: [EmployerStaffDocument],
    awaitRefetchQueries: true,
  });

  const [employerStaffInviteMutation, { loading: inviting }] = useEmployerStaffInviteMutation({
    refetchQueries: [EmployerStaffsListDocument],
    awaitRefetchQueries: true,
  });
  const [employerStaffInviteByEmployerAdmin, { loading: invitingByAdmin }] =
    useEmployerStaffInviteByEmployerAdminMutation({
      refetchQueries: [EmployerStaffsListDocument],
      awaitRefetchQueries: true,
    });

  const employerStaff = React.useMemo(
    () => employerStaffQueryData?.employerStaff,
    [employerStaffQueryData?.employerStaff],
  );

  const updateEmployerStaff = React.useCallback(
    async (params: { data: EmployerStaffUpdate; context?: DefaultContext }) => {
      const { data, context } = params;
      if (!employerStaffId) {
        return;
      }

      await employerStaffUpdateMutation({
        variables: {
          where: { id: employerStaffId },
          data,
        },
        context,
      });
    },
    [employerStaffId, employerStaffUpdateMutation],
  );

  const inviteEmployerStaff = React.useCallback(
    async (params: {
      variables: { employerStaffList: InvitedEmployerStaff[]; employerId: string };
      context?: DefaultContext;
    }) => {
      const { variables, context } = params;

      await employerStaffInviteMutation({
        variables,
        context,
      });
    },
    [employerStaffInviteMutation],
  );

  const inviteEmployerStaffByEmployerAdmin = React.useCallback(
    async (params: {
      variables: { employerStaffList: InvitedEmployerStaff[] };
      context?: DefaultContext;
    }) => {
      const { variables, context } = params;

      await employerStaffInviteByEmployerAdmin({
        variables,
        context,
      });
    },
    [employerStaffInviteByEmployerAdmin],
  );

  return {
    employerStaff,
    employerId,
    employerStaffBillingQueryData,
    updateEmployerStaff,
    inviteEmployerStaff,
    inviteEmployerStaffByEmployerAdmin,
    loading,
    employerStaffBillingLoading,
    updating,
    inviting: inviting || invitingByAdmin,
  };
};
